/**
 * Per-step validation for the RFQ Create Wizard.
 *
 * Pure function over the wizard state so it can be unit-tested without
 * rendering, and so RFQCreateWizard stays under the 500-line limit.
 */
import type { BasicDetailsForm, LineItemForm } from './rfqWizardTypes';

export interface WizardValidationState {
  basicDetails: BasicDetailsForm;
  lineItems: LineItemForm[];
  /** The NDA file picked on the Certificates & NDA step, if any. */
  ndaFile: File | null;
}

/** Shown when an NDA-required RFQ is advanced or sent without its NDA file. */
export const NDA_MISSING_MESSAGE =
  'Upload the NDA document suppliers will be asked to accept, or untick "NDA required" in Basic Details.';

/** Shown when Required Date is earlier than the RFQ Deadline (same words as the API's error). */
export const REQUIRED_DATE_BEFORE_DEADLINE_MESSAGE = "Required Date can't be before the RFQ Deadline.";

/**
 * CR-6203: whether Required Date is set and earlier than the RFQ Deadline —
 * goods can't be needed before the quotes for them are due.
 *
 * @param basicDetails - the Basic Details step's values (ISO dates compare as strings)
 * @returns true when the dates are the wrong way round
 */
export function requiredDateBeforeDeadline(basicDetails: BasicDetailsForm): boolean {
  const { required_delivery_date: required, response_deadline: deadline } = basicDetails;
  return !!required && !!deadline && required < deadline;
}

/** CR-6205: shown when a resumed draft still has line files to pick again. */
export const LINE_FILES_TO_REPICK_MESSAGE =
  "Pick the line files listed in amber again, or remove them. Files aren't kept in a saved draft.";

/**
 * CR-6205: whether any line still lists a file from the saved draft that has
 * not been picked again (or removed).
 *
 * @param lineItems - the wizard's lines
 * @returns true when at least one file is still to be picked again
 */
export function hasLineFilesToRepick(lineItems: LineItemForm[]): boolean {
  return lineItems.some((item) => (item.repick_file_names?.length ?? 0) > 0);
}

/**
 * Whether the given wizard step is complete enough to leave.
 *
 * @param step - 1-based wizard step
 * @param state - the wizard state the step reads
 * @returns true when the user may advance past the step
 */
export function validateWizardStep(step: number, state: WizardValidationState): boolean {
  const { basicDetails, lineItems, ndaFile } = state;
  switch (step) {
    case 1:
      // CR-5466: Title is no longer collected — derived on submit.
      // Description + RFQ Deadline are the only hard requirements
      // (Reference, RFQ Type, RFQ Date are optional per Sarah's design).
      return !!(basicDetails.description && basicDetails.response_deadline)
        && !requiredDateBeforeDeadline(basicDetails);
    case 2:
      return lineItems.length > 0 && lineItems.every(item =>
        item.item_description && item.part_number && item.quantity > 0
      ) && !hasLineFilesToRepick(lineItems);
    case 4:
      // An NDA-required RFQ must carry the NDA suppliers are asked to
      // accept. Without this gate RFQs were sent with no NDA attached and
      // suppliers hit an acceptance screen they could not get through.
      return !basicDetails.nda_required || !!ndaFile;
    case 3:
    case 5:
    case 6:
      return true;
    default:
      return false;
  }
}
